import { motion } from 'framer-motion'
import { Cookie } from 'lucide-react'
import { ScrollReveal } from '@/components/ui'

/**
 * CookiePolicyPage - Minimal cookie policy
 *
 * Pure black. Pure white. Pure transparency.
 * Matches lukhas.id design language.
 */

export default function CookiePolicyPage() {
  return (
    <div className="pb-24 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-6">
              <Cookie className="w-8 h-8 text-white/30" strokeWidth={1} />
              <span className="text-xs text-white/30 uppercase tracking-widest">Legal</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-white mb-4">
              Cookie Policy
            </h1>
            <p className="text-white/40 font-light">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </motion.div>

          {/* What Are Cookies */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-16 p-8 border border-white/10"
          >
            <h2 className="text-xl font-light text-white mb-4">What Are Cookies?</h2>
            <p className="text-white/60 font-light leading-relaxed text-sm">
              Cookies are small text files stored on your device when you visit our website. They help us
              provide a better user experience by remembering your preferences and analyzing how you use
              our Services.
            </p>
          </motion.section>

          {/* Sections */}
          <div className="space-y-16">
            {/* Essential Cookies */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Essential Cookies
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                These cookies are necessary for the website to function and cannot be disabled:
              </p>
              <div className="space-y-4">
                {[
                  { name: 'Authentication', desc: 'Remember your login session via \u039BiD' },
                  { name: 'Security', desc: 'Protect against CSRF attacks and fraud' },
                  { name: 'Session', desc: 'Maintain your preferences during a session' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 py-3 border-b border-white/5 last:border-0">
                    <span className="text-white/70 font-light text-sm w-32 flex-shrink-0">{item.name}</span>
                    <span className="text-white/40 font-light text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Analytics Cookies */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Performance & Analytics
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                These cookies help us understand how visitors use our Services (with your consent):
              </p>
              <div className="space-y-4">
                {[
                  { name: 'Usage Analytics', desc: 'Track page views, feature usage, and navigation patterns' },
                  { name: 'Performance', desc: 'Measure load times and optimize experience' },
                  { name: 'Error Tracking', desc: 'Identify and fix technical issues' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 py-3 border-b border-white/5 last:border-0">
                    <span className="text-white/70 font-light text-sm w-32 flex-shrink-0">{item.name}</span>
                    <span className="text-white/40 font-light text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Functional Cookies */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Functional Cookies
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                These cookies enhance functionality and personalization (optional):
              </p>
              <div className="space-y-4">
                {[
                  { name: 'Language', desc: 'Remember your language selection' },
                  { name: 'Theme', desc: 'Save dark/light mode preferences' },
                  { name: 'UI Settings', desc: 'Remember dashboard layouts and settings' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 py-3 border-b border-white/5 last:border-0">
                    <span className="text-white/70 font-light text-sm w-32 flex-shrink-0">{item.name}</span>
                    <span className="text-white/40 font-light text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* What We Don't Do */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                What We Don't Do
              </h2>
              <div className="space-y-3">
                {[
                  'We do not use advertising or marketing cookies',
                  'We do not sell cookie data to third parties',
                  'We do not track you across other websites',
                  'We do not use cookies for behavioral profiling without consent',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-white/20" />
                    <span className="text-white/50 font-light text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Managing Cookies */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Managing Cookie Preferences
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                You have control over cookie settings:
              </p>
              <div className="space-y-3">
                {[
                  'Use our Cookie Preferences Center in account settings',
                  'Configure your browser to block or delete cookies',
                  'Opt out of analytics cookies while keeping essential ones',
                  'Clear cookies at any time through browser settings',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1 h-1 bg-white/40" />
                    <span className="text-white/60 font-light text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-white/40 font-light text-sm mt-6">
                Note: Blocking essential cookies may impact functionality.
              </p>
            </ScrollReveal>

            {/* Third-Party Services */}
            <ScrollReveal direction="up" blur>
              <h2 className="text-2xl font-light text-white mb-6 pb-4 border-b border-white/5">
                Third-Party Services
              </h2>
              <p className="text-white/50 font-light mb-6 text-sm">
                We use the following third-party services that may set cookies:
              </p>
              <div className="space-y-4">
                {[
                  { name: 'Analytics', desc: 'Google Analytics (anonymized IP)' },
                  { name: 'Infrastructure', desc: 'Cloudflare (security and performance)' },
                  { name: 'Authentication', desc: '\u039BiD authentication service' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 py-3 border-b border-white/5 last:border-0">
                    <span className="text-white/70 font-light text-sm w-32 flex-shrink-0">{item.name}</span>
                    <span className="text-white/40 font-light text-sm">{item.desc}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Contact */}
            <ScrollReveal direction="up" blur>
              <div className="text-center py-12 border-t border-white/5">
                <h2 className="text-2xl font-light text-white mb-4">Questions?</h2>
                <p className="text-white/40 font-light mb-8">
                  Contact us for cookie-related inquiries.
                </p>
                <a
                  href="mailto:privacy@lukhas.id"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-sm font-medium tracking-wide hover:bg-white/90 transition-colors duration-300"
                >
                  privacy@lukhas.id
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
    </div>
  )
}
